import * as React from 'react';
import { Typography } from '@mui/joy';
import { TreeItemLabel } from '@mui/x-tree-view/TreeItem';
import { useTranslation } from 'react-i18next';
import {
  describeInstrument,
  partyNamesIn,
} from '../../ledger/ledgerInstrument';
import { StatusTooltip } from '../../StatusTooltip';
import { useTreeItemRename } from '../treeRename';
import { InstrumentGlyph } from './InstrumentGlyph';
import { LabelStatus } from './LabelStatus';
import { LabelTag } from './LabelTag';
import { PartyTag } from './PartyTag';
import { RenameInput } from './RenameInput';
import './CustomLabel.scss';

export type CustomLabelProps = {
  children: React.ReactNode;
  /** The tree item the label is for, so it can tell when it is being renamed. */
  itemId: string;
  // The tree's item model: the folder listing's record, whatever its shape.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item?: any;
  /** Last in the name column, at its end edge (e.g. a row's own button). */
  adornment?: React.ReactNode;
};

/** A real file extension: a dot and a short run of letters or digits at the end. */
const EXTENSION = /\.[A-Za-z0-9]{1,8}$/;

/**
 * The name and its extension, split so the extension stays visible while the
 * name ellipsises. Only a file has one, and only a real one counts: a folder
 * named "Licensee Group Co., on behalf of itself and its Affiliates" split at
 * its last dot pinned ", on behalf of ..." as an "extension" and squeezed the
 * start of the name out of sight.
 */
const splitNameAndExtension = (name: string, isFolder: boolean) => {
  const match = isFolder ? null : EXTENSION.exec(name);
  if (match && match.index > 0) {
    return {
      nameWithoutExt: name.slice(0, match.index),
      extension: match[0],
    };
  }
  return { nameWithoutExt: name, extension: '' };
};

/**
 * A tree row's label: its glyph, then its id and name (or the field renaming
 * it) with its tags and, while the file is processing or in trouble, its
 * status. Its parts are cells of the ledger row it sits in, so it lays nothing
 * out itself (see CustomLabel.scss).
 */
export const CustomLabel = React.memo(
  ({ children, itemId, item, adornment, ...other }: CustomLabelProps) => {
    const { t } = useTranslation();
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [tooltipTitle, setTooltipTitle] = React.useState('');
    const rename = useTreeItemRename(itemId);

    const fullName = children?.toString() || '';
    const isFolder = item?.fileType === 'folder';

    // A contract folder's parties come off its signature page and show as a
    // tag, so its title is the contract's own, without them.
    const title: string = isFolder
      ? item?.folderData?.contract_title || fullName
      : fullName;
    const signatures = isFolder ? item?.folderData?.signature_info : undefined;
    const partyNames = React.useMemo(
      () => partyNamesIn(signatures),
      [signatures]
    );

    const { nameWithoutExt, extension } = splitNameAndExtension(
      title,
      isFolder
    );

    const instrument = React.useMemo(() => describeInstrument(item), [item]);

    /* The Duplicates section is where copies are reconciled; the tree only says
       that this file has copies, so the user knows before acting on it. */
    const isDuplicate = !isFolder && !!item?.fileData?.is_duplicate;

    /* Documents the chain refers to but nobody has uploaded ("missing"), or
       amendment numbers the chain skips ("gap"), one row for a whole run of
       either, its tag counting the run; and on a contract row, how many of
       each it has in total — so they show even while the contract is
       collapsed. */
    const missing:
      | {
          reason: 'missing' | 'gap';
          contract: string | null;
          count?: number;
          /** The documents that refer to it, as one phrase ("A, B and C"). */
          referredBy?: string | null;
        }
      | undefined = item?.missing;
    const ownCount = missing?.count ?? 1;
    const missingCount: number = !missing ? item?.missingCount ?? 0 : 0;
    const gapCount: number = !missing ? item?.gapCount ?? 0 : 0;
    const missingTitle = !missing
      ? undefined
      : missing.reason === 'gap' && ownCount > 1
      ? missing.contract
        ? t('{{count}} amendment numbers skipped in {{contract}}', {
            count: ownCount,
            contract: missing.contract,
          })
        : t('{{count}} amendment numbers skipped', { count: ownCount })
      : missing.reason === 'gap'
      ? missing.contract
        ? t('Amendment number skipped in {{contract}}', {
            contract: missing.contract,
          })
        : t('Amendment number skipped')
      : ownCount > 1
      ? missing.referredBy || missing.contract
        ? t(
            '{{count}} documents referenced in {{contract}}, but not uploaded',
            {
              count: ownCount,
              contract: missing.referredBy || missing.contract,
            }
          )
        : t('{{count}} documents referenced, but not uploaded', {
            count: ownCount,
          })
      : missing.referredBy || missing.contract
      ? t('Referenced in {{contract}}, but not uploaded', {
          contract: missing.referredBy || missing.contract,
        })
      : t('Referenced, but not uploaded');

    /**
     * Only truncated names get a tooltip, and that can only be decided by
     * measuring. Measuring on mount cost a forced layout, an extra render and a
     * window `resize` listener for every row in the tree — hundreds of them
     * once expanded. The tooltip asks the question instead: MUI opens on
     * mouseover regardless of the title and simply renders nothing while the
     * title is empty, so measuring in `onOpen` is both cheaper and always up to
     * date (no resize listener needed).
     */
    const handleTooltipOpen = React.useCallback(() => {
      const el = textRef.current;
      if (!el) return;
      const overflowing = el.scrollWidth > el.clientWidth;
      // A contract's title leaves its parties to the tag, so a truncated one
      // is shown back as the folder's full name.
      const next = overflowing
        ? (title !== fullName ? fullName : el.textContent) ?? ''
        : '';
      setTooltipTitle(next);
    }, [fullName, title]);

    const nameLevel = isFolder ? 'body-lg' : 'body-sm';

    return (
      <div
        className={
          missing
            ? 'tree-label-root tree-label-root--missing'
            : 'tree-label-root'
        }
        data-kind={instrument.kind}
      >
        <TreeItemLabel {...other} className="tree-label-line">
          <InstrumentGlyph instrument={instrument} />

          <div className="tree-label-main">
            {instrument.docId && !rename && (
              <span className="tree-ledger-docid">{instrument.docId}</span>
            )}
            <StatusTooltip
              title={tooltipTitle}
              onOpen={handleTooltipOpen}
              disableInteractive
              leaveDelay={0}
            >
              <div className="tree-label-text">
                <div className="tree-label-name">
                  {rename ? (
                    <RenameInput
                      initialName={fullName}
                      error={rename.error}
                      isSaving={rename.isSaving}
                      onCommit={rename.onCommit}
                      onCancel={rename.onCancel}
                    />
                  ) : (
                    <>
                      <Typography
                        level={nameLevel}
                        ref={textRef}
                        className={
                          isFolder
                            ? 'tree-label-name__text tree-label-name__text--folder'
                            : 'tree-label-name__text'
                        }
                      >
                        {nameWithoutExt}
                      </Typography>
                      <Typography
                        level={nameLevel}
                        className={
                          isFolder
                            ? 'tree-label-name__ext tree-label-name__ext--folder'
                            : 'tree-label-name__ext'
                        }
                      >
                        {extension}
                      </Typography>
                      {isDuplicate && (
                        <LabelTag tone="duplicate">{t('Duplicate')}</LabelTag>
                      )}
                      {missing && (
                        <LabelTag tone={missing.reason} title={missingTitle}>
                          {missing.reason === 'gap'
                            ? ownCount > 1
                              ? t('{{count}} gaps', { count: ownCount })
                              : t('Gap')
                            : ownCount > 1
                            ? t('{{count}} missing', { count: ownCount })
                            : t('Missing')}
                        </LabelTag>
                      )}
                      {missingCount > 0 && (
                        <LabelTag tone="missing">
                          {t('{{count}} missing', { count: missingCount })}
                        </LabelTag>
                      )}
                      {gapCount > 0 && (
                        <LabelTag tone="gap">
                          {t('{{count}} gaps', { count: gapCount })}
                        </LabelTag>
                      )}
                    </>
                  )}
                </div>
              </div>
            </StatusTooltip>
            {!rename && <LabelStatus item={item} />}
            {partyNames.length > 0 && !rename && (
              <PartyTag names={partyNames} />
            )}
            {adornment && !rename && (
              <span className="tree-label-adornment">{adornment}</span>
            )}
          </div>
        </TreeItemLabel>
      </div>
    );
  }
);
